"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Html5Qrcode,
  Html5QrcodeSupportedFormats,
  CameraDevice,
} from "html5-qrcode";
import { Flashlight, FlashlightOff, Image, Minus, Plus } from "lucide-react";
import Alert from "../../ui/Alert";
import LoadingIndicator from "../../LoadingIndicator";
import { useBackToCloseModal } from "../../../hooks/useBackToCloseModal";

type Props = {
  open: boolean;
  onClose: () => void;
  onDecoded: (text: string) => void;
};

function isFrontCamera(label: string) {
  const s = label.toLowerCase();
  return (
    s.includes("front") ||
    s.includes("trước") ||
    s.includes("user") ||
    s.includes("selfie")
  );
}

function isBackLabel(label: string) {
  if (isFrontCamera(label)) return false;
  return true;
}

function scoreBack(label: string) {
  const s = label.toLowerCase();
  let score = 0;

  if (isFrontCamera(label)) {
    score -= 200;
  }

  if (
    s.includes("back") ||
    s.includes("rear") ||
    s.includes("environment") ||
    s.includes("world") ||
    s.includes("sau") ||
    s.includes("môi trường")
  ) {
    score += 100;
  }

  if (s.includes("0")) score += 5; // Usually typical main camera

  if (s.includes("main") || s.includes("chính")) score += 30;

  if (s.includes("wide") || s.includes("rộng")) {
    if (s.includes("ultra") || s.includes("siêu")) score -= 30; // Ultra wide is bad for QR
    else score += 20; // normal wide is good
  }

  if (s.includes("macro") || s.includes("cận cảnh")) score -= 40;
  if (s.includes("tele") || s.includes("viễn")) score -= 20;
  if (s.includes("depth") || s.includes("chiều sâu")) score -= 50;

  return score;
}

function getCameraEnvError() {
  if (typeof window === "undefined") return null;

  const isSecure =
    window.isSecureContext ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  if (!isSecure) {
    return "Camera chỉ hoạt động trên HTTPS hoặc localhost.";
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return "Trình duyệt hiện tại không hỗ trợ camera.";
  }

  return null;
}

function isIPhone() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua);
}

export default function QrScannerModalV2({ open, onClose, onDecoded }: Props) {
  const containerId = useMemo(
    () => `qr-reader-${Math.random().toString(36).slice(2)}`,
    []
  );

  const h5Ref = useRef<Html5Qrcode | null>(null);
  const startGenRef = useRef(0);
  const isStartingRef = useRef(false);

  const [error, setError] = useState<string | null>(null);
  const [backCams, setBackCams] = useState<CameraDevice[]>([]);
  const [currentCamId, setCurrentCamId] = useState<string | null>(null);

  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomMin, setZoomMin] = useState<number>(1);
  const [zoomMax, setZoomMax] = useState<number>(1);
  const [zoomVal, setZoomVal] = useState<number>(1);

  const [isCameraStarting, setIsCameraStarting] = useState(false);

  const opts = useMemo(() => {
    return {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 4 / 3,
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
    }
  }, []);

  const stopAndClear = useCallback(async () => {
    const inst = h5Ref.current;
    if (!inst) {
      isStartingRef.current = false;
      return;
    }

    try {
      if (inst.isScanning) {
        await inst.stop();
      }
    } catch { }

    try {
      inst.clear();
    } catch { }

    isStartingRef.current = false;
  }, []);

  const closeModal = useCallback(async () => {
    if (isStartingRef.current) return;
    onClose?.();
    await stopAndClear();
  }, [onClose, stopAndClear]);

  useBackToCloseModal(open, closeModal, "qr-modal");

  const teardown = useCallback(async () => {
    await stopAndClear();
    h5Ref.current = null;
    setTorchSupported(false);
    setZoomSupported(false);
    setTorchOn(false);
    setError(null);
  }, [stopAndClear]);

  const readCapabilities = useCallback(async () => {
    try {
      const caps: any = (h5Ref.current as any)?.getRunningTrackCapabilities?.();

      setTorchSupported(!!(caps && "torch" in caps));

      const z = caps?.zoom;
      if (z && typeof z.min === "number" && typeof z.max === "number") {
        const safeMin = Math.max(1, z.min);
        const safeMax = Math.max(safeMin, z.max);

        setZoomSupported(true);
        setZoomMin(safeMin);
        setZoomMax(safeMax);

        const preferredZoom = isIPhone() ? 5 : 2;
        const initialZoom = Math.max(safeMin, Math.min(preferredZoom, safeMax));

        setZoomVal(initialZoom);

        try {
          await (h5Ref.current as any)?.applyVideoConstraints({
            advanced: [{ zoom: initialZoom }],
          });
        } catch {
          try {
            await (h5Ref.current as any)?.applyVideoConstraints({
              zoom: initialZoom,
            });
          } catch { }
        }
      } else {
        setZoomSupported(false);
        setZoomMin(1);
        setZoomMax(1);
        setZoomVal(1);
      }
    } catch {
      setTorchSupported(false);
      setZoomSupported(false);
      setZoomMin(1);
      setZoomMax(1);
      setZoomVal(1);
    }
  }, []);

  const applyTorch = useCallback(
    async (on: boolean) => {
      if (!torchSupported || !h5Ref.current) return;

      try {
        await (h5Ref.current as any).applyVideoConstraints({
          advanced: [{ torch: on }],
        });
        setTorchOn(on);
      } catch { }
    },
    [torchSupported]
  );

  const applyZoom = useCallback(
    async (z: number) => {
      setError(null);
      setZoomVal(z);

      if (!zoomSupported || !h5Ref.current) return;

      try {
        await (h5Ref.current as any).applyVideoConstraints({
          advanced: [{ zoom: z }],
        });
        setZoomVal(z);
      } catch {
        try {
          await (h5Ref.current as any).applyVideoConstraints({ zoom: z });
          setZoomVal(z);
        } catch { }
      }
    },
    [zoomSupported]
  );

  const clamp = (v: number, min: number, max: number) =>
    Math.min(max, Math.max(min, v));

  const step = 1;

  const decZoom = () => applyZoom(clamp(zoomVal - step, zoomMin, zoomMax));
  const incZoom = () => applyZoom(clamp(zoomVal + step, zoomMin, zoomMax));

  const chooseBestCamera = useCallback(async (devices: CameraDevice[]) => {
    const lastCamId = localStorage.getItem("lastCamId");

    const backOnly = devices.filter((d) => isBackLabel(d.label || ""));
    const listToUse = backOnly.length > 0 ? backOnly : devices;

    if (listToUse.length > 0) {
      if (lastCamId) {
        const remembered = listToUse.find((d) => d.id === lastCamId);
        if (remembered) {
          return {
            mode: "deviceId" as const,
            value: remembered.id,
            backList: listToUse,
          };
        }
      }

      const sorted = [...listToUse].sort(
        (a, b) => scoreBack(b.label || "") - scoreBack(a.label || "")
      );

      return {
        mode: "deviceId" as const,
        value: sorted[0].id,
        backList: listToUse,
      };
    }

    return {
      mode: "facingMode" as const,
      value: "environment",
      backList: [],
    };
  }, []);

  const startWith = useCallback(
    async (
      cameraConfig: { deviceId?: { exact: string } } | { facingMode: string }
    ) => {
      if (!h5Ref.current || isStartingRef.current || h5Ref.current.isScanning) {
        return;
      }

      isStartingRef.current = true;

      try {
        await h5Ref.current.start(
          cameraConfig as any,
          opts,
          async (decodedText: string) => {
            onDecoded(decodedText);

            if (
              "deviceId" in cameraConfig &&
              (cameraConfig as any).deviceId?.exact
            ) {
              localStorage.setItem(
                "lastCamId",
                (cameraConfig as any).deviceId.exact
              );
            }

            onClose?.();
            await stopAndClear();
          },
          () => { }
        );

        const root = document.getElementById(containerId);
        const video = root?.querySelector("video") as HTMLVideoElement | null;
        if (video) {
          video.setAttribute("playsinline", "true");
          video.setAttribute("webkit-playsinline", "true");
          video.muted = true;
        }

        await readCapabilities();
      } catch (e: any) {
        const msg =
          e?.name === "NotAllowedError"
            ? isIPhone()
              ? "iPhone đang chặn quyền camera. Hãy vào Safari > aA > Website Settings > Camera > Allow rồi thử lại."
              : "Bạn đã chặn quyền camera. Hãy bật lại quyền camera cho website rồi thử lại."
            : e?.name === "NotReadableError"
              ? isIPhone()
                ? "Safari chưa mở được camera hoặc camera đang bị ứng dụng khác dùng."
                : "Camera đang được ứng dụng khác sử dụng hoặc không thể khởi tạo."
              : e?.name === "OverconstrainedError"
                ? "Không tìm thấy camera phù hợp. Đang thử camera mặc định."
                : e?.message || "Không thể bật camera.";

        setError(msg);
        throw e;
      } finally {
        isStartingRef.current = false;
      }
    },
    [opts, onDecoded, onClose, stopAndClear, readCapabilities, containerId]
  );

  const chooseAndStartBestBack = useCallback(
    async (list: CameraDevice[]) => {
      const picked = await chooseBestCamera(list);

      setBackCams(picked.backList);

      if (picked.mode === "deviceId") {
        setCurrentCamId(picked.value);
        await startWith({ deviceId: { exact: picked.value } });
        return;
      }

      setCurrentCamId(null);
      await startWith({ facingMode: "environment" });
    },
    [chooseBestCamera, startWith]
  );

  const handleSwitchCamera = useCallback(
    async (id: string) => {
      if (!id || id === currentCamId) return;

      setError(null);
      setCurrentCamId(id);
      setZoomSupported(false);
      setIsCameraStarting(true);

      try {
        await applyTorch(false);
        await stopAndClear();
        await startWith({ deviceId: { exact: id } });
      } finally {
        setIsCameraStarting(false);
      }
    },
    [applyTorch, currentCamId, startWith, stopAndClear]
  );

  const handlePickFile = useCallback(
    async (f: File | null) => {
      if (!f || !h5Ref.current) return;

      if (f.size > 10 * 1024 * 1024) {
        setError("Ảnh quá lớn (>10MB)");
        return;
      }

      const resumeId = currentCamId;
      setZoomSupported(false);
      setIsCameraStarting(true);

      await stopAndClear();

      try {
        const anyH5: any = h5Ref.current;
        const res =
          typeof anyH5.scanFileV2 === "function"
            ? await anyH5.scanFileV2(f, false)
            : await anyH5.scanFile(f, true);

        const txt = res?.decodedText ?? (typeof res === "string" ? res : null);

        if (txt) {
          onDecoded(txt);
          onClose?.();
          setIsCameraStarting(false);
          await stopAndClear();
          return;
        }

        setError("Không đọc được mã từ ảnh.");
      } catch {
        setError("Không đọc được mã từ ảnh. Hãy dùng ảnh rõ hơn/đủ sáng.");
      }

      try {
        if (resumeId) {
          await startWith({ deviceId: { exact: resumeId } });
        } else {
          await startWith({ facingMode: "environment" });
        }
      } catch {
      } finally {
        setIsCameraStarting(false);
      }
    },
    [currentCamId, onClose, onDecoded, startWith, stopAndClear]
  );

  useEffect(() => {
    const onHide = async () => {
      if (document.visibilityState === "hidden") {
        await stopAndClear();
      }
    };

    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);

    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
    };
  }, [stopAndClear]);

  useEffect(() => {
    return () => {
      stopAndClear();
    };
  }, [stopAndClear]);

  useEffect(() => {
    if (typeof window === "undefined" || !open) return;

    if (!h5Ref.current) {
      h5Ref.current = new Html5Qrcode(containerId, false);
    }

    return () => {
      startGenRef.current++;
      (async () => {
        await stopAndClear();
        h5Ref.current = null;
      })();
    };
  }, [containerId, open, stopAndClear]);

  useEffect(() => {
    if (typeof window === "undefined" || !open) return;

    let cancelled = false;
    const myGen = ++startGenRef.current;

    const run = async () => {
      setIsCameraStarting(true);
      setTorchSupported(false);
      setZoomSupported(false);
      setTorchOn(false);
      setError(null);

      try {
        const envError = getCameraEnvError();
        if (envError) {
          setError(envError);
          return;
        }

        let tries = 12;
        while (!cancelled && myGen === startGenRef.current && tries-- > 0) {
          const el = document.getElementById(containerId);
          if (el && el.clientWidth >= 50 && el.clientHeight >= 50) break;
          await new Promise((r) => requestAnimationFrame(r));
        }

        if (cancelled || myGen !== startGenRef.current) return;

        let devices = await Html5Qrcode.getCameras();

        if (cancelled || myGen !== startGenRef.current) return;

        if (devices.length === 0) {
          await startWith({ facingMode: "environment" });
          return;
        }

        const hasLabels = devices.some((d) => (d.label || "").trim().length > 0);

        if (!hasLabels) {
          try {
            await startWith({ facingMode: "environment" });

            const refreshed = await Html5Qrcode.getCameras();
            if (refreshed.length > 0) {
              devices = refreshed;

              const best = await chooseBestCamera(devices);
              setBackCams(best.backList);

              if (best.mode === "deviceId" && best.value) {
                setCurrentCamId(best.value);
                await stopAndClear();
                await startWith({ deviceId: { exact: best.value } });
              }
            }

            return;
          } catch {
            try {
              const refreshed = await Html5Qrcode.getCameras();
              if (refreshed.length > 0) {
                devices = refreshed;
              }
            } catch { }
          }
        }

        if (cancelled || myGen !== startGenRef.current) return;

        try {
          await chooseAndStartBestBack(devices);
        } catch (e: any) {
          const overConstrained =
            e?.name === "OverconstrainedError" ||
            String(e?.message || "")
              .toLowerCase()
              .includes("overconstrained");

          if (!overConstrained) throw e;

          setCurrentCamId(null);
          await startWith({ facingMode: "environment" });
        }
      } catch (e: any) {
        const msg =
          e?.name === "NotAllowedError"
            ? isIPhone()
              ? "iPhone đang chặn quyền camera. Hãy vào Safari > aA > Website Settings > Camera > Allow. Nếu đang mở trong Zalo/Facebook/Messenger, hãy mở bằng Safari."
              : "Bạn đã chặn quyền camera. Hãy bật lại quyền camera cho website rồi thử lại."
            : e?.name === "NotReadableError"
              ? isIPhone()
                ? "Safari chưa mở được camera hoặc camera đang bị ứng dụng khác dùng."
                : "Camera đang được ứng dụng khác sử dụng hoặc không thể khởi tạo."
              : e?.name === "NotFoundError"
                ? "Không tìm thấy camera trên thiết bị."
                : e?.message || "Không thể bật camera.";

        setError(msg);
      } finally {
        if (!cancelled && myGen === startGenRef.current) {
          setIsCameraStarting(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [open, containerId, chooseAndStartBestBack, startWith]);

  useEffect(() => {
    const form = document.getElementById("form-info");

    if (open) {
      if (form) {
        form.classList.add("z-[100]");
      }
      document.body.classList.add("overflow-hidden");
    } else {
      if (form) {
        form.classList.remove("z-[100]");
      }
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
      if (form) {
        form.classList.remove("z-[100]");
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onClick={closeModal}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-xl p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-lg font-semibold">Quét mã QR CCCD</span>
          <button
            onClick={closeModal}
            className="px-3 py-1 rounded-lg border hover:bg-gray-50"
          >
            Đóng
          </button>
        </div>

        <div className="relative rounded-lg" style={{ aspectRatio: "4 / 3" }}>
          <div id={containerId} className="w-full qr-slot relative z-10" />
          <div className="absolute inset-0 bottom-0">
            <LoadingIndicator />
          </div>

          {zoomSupported && (
            <div className="flex w-[70%] items-end gap-4 absolute z-20 bottom-5 left-1/2 -translate-x-1/2">
              <button
                type="button"
                onClick={decZoom}
                className="p-1 cursor-pointer rounded-full bg-white disabled:opacity-50"
                disabled={zoomVal <= zoomMin}
                aria-label="Giảm zoom"
                title="Giảm zoom"
              >
                <Minus className="w-4 h-4" />
              </button>

              <div className="w-full">
                <div className="flex items-center justify-between">
                  <h2 className="text-white text-sm">Zoom</h2>
                  <span className="text-white text-sm text-center">
                    {zoomVal}
                  </span>
                </div>

                <div className="relative w-full">
                  <input
                    type="range"
                    min={zoomMin}
                    max={zoomMax}
                    step={step}
                    value={zoomVal}
                    onChange={(e) => applyZoom(Number(e.target.value))}
                    className="w-full bg-[#4b5563] h-1 rounded-lg appearance-none cursor-pointer slider"
                    style={{
                      background: `linear-gradient(to right, white 0%, white ${zoomMax > zoomMin
                          ? ((zoomVal - zoomMin) / (zoomMax - zoomMin)) * 100
                          : 0
                        }%, #4b5563 ${zoomMax > zoomMin
                          ? ((zoomVal - zoomMin) / (zoomMax - zoomMin)) * 100
                          : 0
                        }%, #4b5563 100%)`,
                    }}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={incZoom}
                className="p-1 cursor-pointer bg-white rounded-full disabled:opacity-50"
                disabled={zoomVal >= zoomMax}
                aria-label="Tăng zoom"
                title="Tăng zoom"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center w-full gap-2">
          {backCams.length > 0 && (
            <div className="flex items-center w-full gap-4">
              <span className="text-sm text-gray-700 shrink-0">
                Chọn camera
              </span>
              <select
                className="w-full border rounded px-4 py-2 text-sm text-gray-700 bg-white border border-slate-200 focus:border-green-200 focus:ring-1 focus:ring-green-500 no-outline transition-all duration-200"
                value={currentCamId ?? ""}
                disabled={isCameraStarting}
                onChange={(e) => handleSwitchCamera(e.target.value)}
              >
                <option value="" disabled>
                  Chọn camera
                </option>
                {backCams.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label || c.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!!error && <span className="text-xs text-red-600">{error}</span>}
        </div>

        <div className="flex mt-3 w-full">
          <div className="flex items-center justify-center gap-2 flex-1 w-full">
            <div className="cursor-pointer w-fit">
              <label className="flex flex-col cursor-pointer items-center justify-center">
                <div
                  className={`p-3 rounded-full ${isCameraStarting ? "bg-gray-400" : "bg-green-400"
                    }`}
                >
                  <Image className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm px-2 py-1 text-foreground">
                  Tải ảnh lên
                </p>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  disabled={isCameraStarting}
                  onChange={(e) => handlePickFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 flex-1 w-full">
            <div
              className="cursor-pointer w-fit"
              onClick={() => applyTorch(!torchOn)}
            >
              <div className="flex flex-col cursor-pointer items-center justify-center">
                <div
                  className={`p-3 rounded-full ${torchSupported
                      ? torchOn
                        ? "bg-green-400"
                        : "bg-gray-400"
                      : "bg-gray-400"
                    }`}
                >
                  {torchOn ? (
                    <Flashlight className="w-6 h-6 text-white" />
                  ) : (
                    <FlashlightOff className="w-6 h-6 text-white" />
                  )}
                </div>
                <p className="text-sm px-2 py-1 text-foreground">Đèn pin</p>
              </div>
            </div>
          </div>
        </div>

        <Alert
          variant="info"
          msg="Đặt mã QR khớp với khung. Nếu không quét được: Tải lên ảnh QR hoặc bật đèn pin / tăng Zoom"
          className="mb-4 py-3 text-sm"
        />
      </div>
    </div>
  );
}